import { v4 } from 'uuid'

type INPUTSELECTOR<State extends any, R> = (state: State) => R
type DEPRESULT<S extends Array<INPUTSELECTOR<any, any>>> = {
  [Index in keyof S]: ReturnType<S[Index]>
}

type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (k: infer I) => void ? I : never

type DEPSTATE<S extends Array<INPUTSELECTOR<any, any>>> = UnionToIntersection<Parameters<S[number]>[number]>

type MemoizedSelector<S extends any, R> = {
  (state: S): R
  id: string
  depsSelector: (state: S) => any[]
}

export const createSelector = <const I extends Array<INPUTSELECTOR<any, any>>, R>(
  deps: I,
  mapper: (...deps: DEPRESULT<I>) => R,
  {
    resultEqual,
    cache,
  }: {
    resultEqual?: (a: R, b: R) => boolean
    cache?: {}
  } = {}
): ((state: DEPSTATE<I>) => R) => {
  const getMemdeps = () => {
    return deps.map(dep => {
      if (isMemoSelector(dep)) return dep
      return memoize(dep)
    }) as any
  }
  const id = `sel-${v4()}`
  const cacheSymbol = Symbol(id)
  const getCache = (
    state: any
  ): {
    lastDepResults: DEPRESULT<I> | null
    lastResult: { val: R } | null
    memdeps: I
  } => {
    const c = cache || state.cache
    if (!c) throw new Error('Memo selector requires cache reducer')
    if (!c[cacheSymbol]) {
      c[cacheSymbol] = {
        lastDepResults: null,
        lastResult: null,
        memdeps: getMemdeps(),
      }
    }
    return c[cacheSymbol]
  }

  const depsSelector = (state: DEPSTATE<I>) => {
    const cache = getCache(state)
    return cache.memdeps.map(dep => dep(state))
  }

  const memoized: MemoizedSelector<DEPSTATE<I>, R> = (state: DEPSTATE<I>) => {
    const cache = getCache(state)
    const results = depsSelector(state) as DEPRESULT<I>
    if (cache.lastDepResults && cache.lastResult && arrEqual(results, cache.lastDepResults)) return cache.lastResult.val
    cache.lastDepResults = results
    const result = mapper(...results)
    if (cache.lastResult && resultEqual && resultEqual(result, cache.lastResult.val)) {
      return cache.lastResult.val
    }

    cache.lastResult = { val: result }
    return result
  }

  memoized.id = id
  memoized.depsSelector = depsSelector
  memoized[MEMOSELECTOR_SYMBOL] = true

  return memoized
}

const MEMOSELECTOR_SYMBOL = Symbol('MEMOSELECTOR')

export const isMemoSelector = (obj: any): obj is MemoizedSelector<any, any> => {
  return typeof obj === 'function' && obj[MEMOSELECTOR_SYMBOL] === true
}

export const asMemoSelector = (obj: any): MemoizedSelector<any, any> | null => {
  if (isMemoSelector(obj)) return obj
  return null
}

const memoize = <A, R>(fn: (arg: A) => R): ((arg: A) => R) => {
  let lastCall: { arg: A; r: R } | null = null
  return (arg: A) => {
    if (lastCall && lastCall.arg === arg) return lastCall.r
    const r = fn(arg)
    lastCall = { arg, r }
    return r
  }
}

const arrEqual = <T extends Array<any>>(arr1: T, arr2: T) => {
  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) return false
  }
  return true
}
